import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../config';

interface AuthResponse {
  token: {
    access_token: string;
    refresh_token: string;
  };
  attendee: {
    id: number;
    title: string;
  };
}

export function PinInput() {
  const [pin, setPin] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [facilityInitials, setFacilityInitials] = useState<string>('');
  const [facilityLoading, setFacilityLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Get facility info from localStorage
  const facilityId = localStorage.getItem('selectedFacilityId');
  
  useEffect(() => {
    if (!facilityId) {
      navigate('/select-facility');
      return;
    }

    setFacilityLoading(true);
    // Fetch facility details
    fetch(`${config.API_URL}/facilities/${facilityId}`, {
      headers: {
        'Accept': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load facility details');
        }
        return response.json();
      })
      .then(data => {
        console.log(data)
        setFacilityInitials(data.result.initials);
        localStorage.setItem('facilityInitials', data.result.initials);
      })
      .catch(error => {
        console.error('Error loading facility:', error);
        setError('Failed to load facility details. Please try again.');
        setTimeout(() => navigate('/select-facility'), 3000);
      })
      .finally(() => {
        setFacilityLoading(false);
      });
  }, [facilityId, navigate]);

  const handleNumberClick = (number: number) => {
    if (pin.length < 4) {
      setPin(prev => prev + number);
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setPin('');
  };

  const showWelcomeNotification = (name: string) => {
    toast.success(`Hello again ${name}! Welcome back!`, {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const handleSubmit = async () => {
    if (pin.length === 4 && facilityId) {
      setLoading(true);
      try {
        const fullLabel = `${facilityInitials}${pin}`;
        const response = await fetch(`${config.API_URL}/attendees/auth/pin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            facility_id: parseInt(facilityId),
            label: fullLabel  // Send the full label instead of just the PIN
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Invalid PIN');
        }

        const data: AuthResponse = await response.json();
        
        // Extract name from title (format: "# ID - Name")
        const name = data.attendee.title.split(' - ')[1];
        
        // Show welcome notification
        showWelcomeNotification(name);
        
        // Login with the new token format
        login(data.token.access_token, {
          id: data.attendee.id,
          name: name,
          email: '',
          status: 'ACTIVE'
        });
        
        // Small delay to allow the notification to be seen
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);

      } catch (error) {
        console.error('Error validating PIN:', error);
        setError(error instanceof Error ? error.message : 'Invalid PIN. Please try again.');
        setPin('');
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (pin.length === 4) {
      handleSubmit();
    }
  }, [pin]); // eslint-disable-line react-hooks/exhaustive-deps

  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

  return (
    <div className="container pin-input">
      <h1>Enter PIN</h1>
      {error && <div className="error">{error}</div>}
      
      <div className="pin-display">
        {facilityLoading ? (
          <div className="loading-initials">Loading...</div>
        ) : (
          <div className="facility-initials">{facilityInitials}</div>
        )}
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="pin-digit">
            {pin[i] ? '•' : ''}
          </div>
        ))}
      </div>

      <div className="numpad">
        <div className="numpad-grid">
          {numbers.map(number => (
            <button
              key={number}
              onClick={() => handleNumberClick(number)}
              className="numpad-button"
              disabled={loading || pin.length >= 4}
            >
              {number}
            </button>
          ))}
        </div>
        <div className="numpad-controls">
          <button
            onClick={handleClear}
            className="numpad-button control"
            disabled={loading || !pin.length}
          >
            Clear
          </button>
          <button
            onClick={handleBackspace}
            className="numpad-button control"
            disabled={loading || !pin.length}
          >
            ←
          </button>
        </div>
      </div>

      {/* <button
        onClick={() => navigate('/select-facility')}
        className="change-facility-button"
      >
        Change Facility
      </button> */}
    </div>
  );
} 